import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Btn, Eyebrow } from "../components/Primitives";
import GoogleButton, { startGoogleAuth } from "../account/GoogleButton";
import { useAccount } from "../account/AccountContext";
import { formatAuthError } from "../lib/accountsApi";
import AppShell from "../components/AppShell";
import PasswordInput from "../components/PasswordInput";

export default function Login() {
    const { login, account } = useAccount();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (account) {
        setTimeout(() => navigate("/"), 0);
    }

    const submit = async (e) => {
        e?.preventDefault?.();
        setSubmitting(true);
        setError(null);
        try {
            await login(email.trim(), password);
            toast.success("Welcome back");
            navigate("/");
        } catch (err) {
            setError(formatAuthError(err?.response?.data?.detail));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AppShell>
            <div className="flex-1 flex flex-col px-7 py-10" data-testid="login-screen">
                <Eyebrow>Welcome back</Eyebrow>
                <h1 className="mt-2 font-display text-4xl text-burgundy leading-tight">
                    Sign <span className="italic">in</span> to Ever After.
                </h1>

                <div className="mt-7">
                    <GoogleButton onClick={() => startGoogleAuth("/auth/callback")} />
                </div>

                <div className="my-5 flex items-center gap-3">
                    <div className="flex-1 h-px bg-burgundy/15" />
                    <span className="text-[10px] tracking-[0.32em] uppercase text-ink-light">or with email</span>
                    <div className="flex-1 h-px bg-burgundy/15" />
                </div>

                <form onSubmit={submit} className="space-y-4">
                    <Field label="Email">
                        <input
                            data-testid="login-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={e => setEmail(e.target.value)}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>
                    <Field label="Password">
                        <PasswordInput
                            testId="login-password"
                            autoComplete="current-password"
                            value={password}
                            onChange={e => setPassword(e.target.value)}
                            className="w-full bg-cream-soft border border-burgundy/20 focus:border-burgundy py-3 px-3 text-base text-ink focus:outline-none"
                        />
                    </Field>
                    {error && (
                        <div data-testid="login-error" className="text-xs text-burgundy bg-rose/20 border border-burgundy/20 px-3 py-2">
                            {error}
                        </div>
                    )}
                    <Btn data-testid="login-submit-btn" type="submit" disabled={submitting || !email || !password}>
                        {submitting ? "Signing in…" : "Sign in"}
                    </Btn>
                </form>

                <div className="mt-6 flex items-center justify-between text-[11px] tracking-[0.24em] uppercase">
                    <Link to="/forgot-password" data-testid="login-forgot-link" className="text-burgundy/80 underline underline-offset-4">
                        Forgot password
                    </Link>
                    <Link to="/" data-testid="login-back-home" className="text-ink-muted">
                        ← Take the assessment
                    </Link>
                </div>
                <p className="mt-5 text-center text-[11px] tracking-[0.24em] uppercase">
                    <span className="text-ink-muted">New here? </span>
                    <Link to="/signup" data-testid="login-signup-link" className="tap-link text-burgundy underline underline-offset-4">
                        Create account
                    </Link>
                </p>
                <p className="mt-4 text-center text-[10px] tracking-[0.32em] uppercase text-ink-light">
                    <Link to="/privacy" data-testid="login-privacy-link" className="tap-link hover:text-burgundy hover:underline underline-offset-4">
                        Privacy policy
                    </Link>
                </p>
            </div>
        </AppShell>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink-muted mb-1.5">{label}</div>
            {children}
        </label>
    );
}
